import React from "react";
import { Button, type ButtonProps } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Define types for menu items and groups
type MenuItem = {
  id: string;
  label: string | React.ReactNode;
  shortcut?: string;
  onClick?: () => void;
  disabled?: boolean;
  icon?: React.ReactNode;
};

type SubMenuItem = MenuItem & {
  items: MenuItem[];
};

type MenuGroup = {
  label?: string;
  items: (MenuItem | SubMenuItem)[];
};

type ReusableDropdownMenuProps = {
  triggerButton: React.ReactNode | ButtonProps;
  menuLabel?: string;
  groups: MenuGroup[];
  align?: "start" | "end" | "center";
  className?: string;
};

const isSubMenuItem = (item: MenuItem | SubMenuItem): item is SubMenuItem => {
  return "items" in item;
};

export function ReusableDropdownMenu({
  triggerButton,
  menuLabel,
  groups,
  align = "end",
  className = "w-56",
}: ReusableDropdownMenuProps) {
  const renderMenuItem = (item: MenuItem | SubMenuItem) => {
    if (isSubMenuItem(item)) {
      return (
        <DropdownMenuSub key={item.id}>
          <DropdownMenuSubTrigger>{item.label}</DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              {item.items.map(renderMenuItem)}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
      );
    }

    return (
      <DropdownMenuItem
        key={item.id}
        disabled={item.disabled}
        onClick={item.onClick}
      >
        {item.icon && <span className="mr-2">{item.icon}</span>}
        {item.label}
        {item.shortcut && (
          <DropdownMenuShortcut>{item.shortcut}</DropdownMenuShortcut>
        )}
      </DropdownMenuItem>
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {React.isValidElement(triggerButton) ? (
          triggerButton
        ) : (
          <Button variant="outline" {...(triggerButton as ButtonProps)}>
            {(triggerButton as ButtonProps).children || "Open"}
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent className={className} align={align}>
        {menuLabel && <DropdownMenuLabel>{menuLabel}</DropdownMenuLabel>}
        {groups.map((group, index) => (
          <React.Fragment key={index}>
            {index > 0 && <DropdownMenuSeparator />}
            {group.label && (
              <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
            )}
            <DropdownMenuGroup>
              {group.items.map(renderMenuItem)}
            </DropdownMenuGroup>
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
